import React, { useState, useEffect, Suspense, useRef } from 'react';
import { Shield, Activity, Lock, Loader2, Box, ExternalLink, PlusCircle } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { getCategoryImage } from '../utils/helpers';
import { toast } from 'sonner';
import { useWeb3 } from '../context/Web3Context';

// Lazy load the 3D scene
const ThreeScene = React.lazy(() => import('./ThreeScene'));

export const DigitalTwinViewer = ({ asset, token, ownershipPercentage = 100 }) => {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [modelData, setModelData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const animFrameRef = useRef(null);

  // Resolve image: use asset image_url, or fall back to category stock photo
  const resolvedImageUrl = asset?.image_url || getCategoryImage(asset?.category);

  // Fetch existing 3D model on mount
  useEffect(() => {
    if (asset?.id) {
      api.get3DModel(asset.id).then(res => {
        if (res.data?.status === 'completed' && res.data?.model_url) {
          setModelData(res.data);
        }
      }).catch(() => {});
    }
  }, [asset?.id]);

  // Subtle idle sway (±4 degrees) when not hovering
  useEffect(() => {
    if (showModel || isHovering) {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      return;
    }
    let startTime = null;
    const animate = (ts) => {
      if (!startTime) startTime = ts;
      const elapsed = (ts - startTime) / 1000;
      const yAngle = Math.sin(elapsed * 0.6) * 4;
      const xAngle = Math.sin(elapsed * 0.4) * 1.5;
      setRotation({ x: xAngle, y: yAngle });
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, [isHovering, showModel]);

  const handleMouseMove = (e) => {
    if (showModel) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientY - rect.top) / rect.height - 0.5) * 20;
    const y = ((e.clientX - rect.left) / rect.width - 0.5) * 30;
    setRotation({ x, y });
  };

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  const handleGenerate3D = async () => {
    if (!resolvedImageUrl) return;
    setGenerating(true);
    try {
      const res = await api.generate3DModel(asset.id, resolvedImageUrl);
      if (res.data?.model_url) {
        setModelData(res.data);
        setShowModel(true);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('balance')) {
        toast.error('3D service balance exhausted. Please top up at fal.ai/dashboard/billing.');
      } else {
        toast.error(detail || '3D model generation failed. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const auraGold = ownershipPercentage >= 80;
  const auraColor = auraGold 
    ? '0 0 40px rgba(212, 175, 55, 0.5), 0 0 80px rgba(212, 175, 55, 0.2)' 
    : `0 0 40px rgba(212, 175, 55, ${ownershipPercentage / 200}), 0 0 80px rgba(98, 126, 234, ${(100 - ownershipPercentage) / 200})`;
  const auraGradient = auraGold ? 'from-gold/30 via-gold/10 to-gold/30' : 'from-gold/20 via-[#627EEA]/20 to-gold/20';
  const imageUrl = resolvedImageUrl;

  return (
    <div className="relative" data-testid="digital-twin-viewer">
      {/* Verification Pulse Ring */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
        <div className="twin-pulse-ring absolute w-[110%] h-[110%] rounded-lg border border-gold/20" />
        <div className="twin-pulse-ring-delayed absolute w-[115%] h-[115%] rounded-lg border border-gold/10" />
      </div>

      {/* 3D Model View or CSS Card View */}
      {showModel && modelData?.model_url ? (
        <div className="relative" style={{ boxShadow: auraColor }}>
          <div className={`absolute -inset-2 bg-gradient-to-r ${auraGradient} rounded-lg blur-xl opacity-60 animate-pulse`} style={{ animationDuration: '3s' }} />
          <div className="relative bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg overflow-hidden">
            <div className="aspect-square">
              <Suspense fallback={
                <div className="w-full h-full flex items-center justify-center bg-[#0A0A0A]">
                  <Loader2 size={24} className="text-gold animate-spin" />
                </div>
              }>
                <ThreeScene modelUrl={modelData.model_url} />
              </Suspense>
            </div>
            {/* Status Badge */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-black/60 backdrop-blur-sm border border-gold/30 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 twin-heartbeat" />
              <span className="text-[10px] text-emerald-400 uppercase tracking-wider">3D Active</span>
            </div>
            <button onClick={() => setShowModel(false)} className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-sm border border-[#333] rounded-full text-[10px] text-muted-foreground hover:text-white">
              2D View
            </button>
            <InfoBar asset={asset} token={token} ownershipPercentage={ownershipPercentage} />
          </div>
        </div>
      ) : (
        <div
          className="relative cursor-grab active:cursor-grabbing"
          style={{ perspective: '1200px' }}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="relative transition-transform duration-100 ease-out"
            style={{ transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`, transformStyle: 'preserve-3d' }}
          >
            <div className={`absolute -inset-2 bg-gradient-to-r ${auraGradient} rounded-lg blur-xl opacity-60 animate-pulse`} style={{ animationDuration: '3s' }} />
            <div className="relative bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg overflow-hidden" style={{ boxShadow: auraColor }}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={imageUrl} alt={asset?.name || 'Digital Twin'} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: `linear-gradient(${135 + rotation.y * 2}deg, rgba(255,255,255,0.3) 0%, transparent 50%)` }} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 bg-black/60 backdrop-blur-sm border border-gold/30 rounded-full cursor-help">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 twin-heartbeat" />
                      <span className="text-[10px] text-emerald-400 uppercase tracking-wider">Vault Active</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[200px] bg-[#1A1A1A] border border-[#333] text-white">
                    <p className="text-xs">Your Digital Twin is live and actively monitored on the blockchain.</p>
                  </TooltipContent>
                </Tooltip>
                {token && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-sm border border-[#627EEA]/30 rounded-full cursor-help">
                        <span className="text-[10px] text-[#627EEA] font-mono">#{token.token_sequence || token.id?.slice(0, 8)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[200px] bg-[#1A1A1A] border border-[#333] text-white">
                      <p className="text-xs">Your unique token identifier on the blockchain. This is your asset's digital fingerprint.</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <InfoBar asset={asset} token={token} ownershipPercentage={ownershipPercentage} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Shared info bar component with educational tooltips
const InfoBar = ({ asset, token, ownershipPercentage }) => (
  <div className="p-4 space-y-3">
    <div>
      <h3 className="text-white font-serif text-lg">{asset?.name}</h3>
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="text-xs text-muted-foreground uppercase tracking-wider cursor-help">{asset?.category} Digital Twin</p>
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px] bg-[#1A1A1A] border border-[#333] text-white">
          <p className="text-xs">A blockchain-secured digital copy of your physical asset, verifying its authenticity and ownership history.</p>
        </TooltipContent>
      </Tooltip>
    </div>
    <div>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex justify-between text-[10px] mb-1 cursor-help">
            <span className="text-muted-foreground uppercase tracking-wider">Ownership</span>
            <span className="text-gold font-mono">{ownershipPercentage.toFixed(1)}%</span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px] bg-[#1A1A1A] border border-[#333] text-white">
          <p className="text-xs">Your share of this Digital Twin. 100% means you hold full ownership of the tokenized asset.</p>
        </TooltipContent>
      </Tooltip>
      <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${ownershipPercentage}%`, background: ownershipPercentage >= 80 ? 'linear-gradient(90deg, #D4AF37, #F4D03F)' : `linear-gradient(90deg, #D4AF37 0%, #627EEA ${100 - ownershipPercentage}%, #D4AF37 100%)` }} />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 p-2 bg-[#0F0F0F] rounded cursor-help"><Shield size={10} className="text-emerald-400" /><span className="text-[9px] text-emerald-400 uppercase">Insured</span></div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[200px] bg-[#1A1A1A] border border-[#333] text-white">
          <p className="text-xs">Your physical asset is covered by institutional-grade insurance while in secure custody.</p>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 p-2 bg-[#0F0F0F] rounded cursor-help"><Activity size={10} className="text-gold" /><span className="text-[9px] text-gold uppercase">Oracle Live</span></div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px] bg-[#1A1A1A] border border-[#333] text-white">
          <p className="text-xs">Real-time market valuation powered by AI. Your asset's value is continuously tracked against live market data.</p>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 p-2 bg-[#0F0F0F] rounded cursor-help"><Lock size={10} className="text-[#627EEA]" /><span className="text-[9px] text-[#627EEA] uppercase">On-Chain</span></div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px] bg-[#1A1A1A] border border-[#333] text-white">
          <p className="text-xs">Secured on the blockchain. Your ownership is cryptographically verified and cannot be tampered with.</p>
        </TooltipContent>
      </Tooltip>
    </div>
    <OnChainRegistry asset={asset} token={token} />
  </div>
);

// Polygon registry actions: View on Polygonscan + Add to MetaMask. Renders
// nothing until the token has been minted on-chain (tx_hash / contract_address set).
const OnChainRegistry = ({ asset, token }) => {
  const { addDigitalTwinToMetaMask, isMetaMaskInstalled } = useWeb3();
  if (!token) return null;
  const { tx_hash: txHash, contract_address: contractAddress, token_id: tokenId, chain_id: chainId } = token;
  if (!txHash && !contractAddress) return null;

  // Polygon explorer selection (fallback to Amoy which is the current default)
  const explorerBase = chainId === 137
    ? 'https://polygonscan.com'
    : 'https://amoy.polygonscan.com';
  const txUrl = txHash ? `${explorerBase}/tx/${txHash}` : null;
  const contractUrl = contractAddress ? `${explorerBase}/address/${contractAddress}` : null;

  const handleAddToMetaMask = async () => {
    if (!contractAddress) {
      toast.error('Registry contract not set yet');
      return;
    }
    await addDigitalTwinToMetaMask({
      contractAddress,
      tokenId: tokenId ?? 0,
      name: asset?.name,
      imageUrl: asset?.image_url,
    });
  };

  return (
    <div className="pt-3 mt-1 border-t border-[#1A1A1A] space-y-2" data-testid="on-chain-registry">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Polygon Registry</span>
        <span className="text-[9px] text-[#627EEA] font-mono">{chainId === 137 ? 'Mainnet' : 'Amoy'}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {(txUrl || contractUrl) && (
          <a
            href={txUrl || contractUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#0F0F0F] hover:bg-[#151515] border border-[#1A1A1A] hover:border-[#627EEA]/40 rounded text-[10px] text-[#627EEA] transition-colors"
            data-testid="view-polygonscan-btn"
          >
            <ExternalLink size={10} />
            View on Polygonscan
          </a>
        )}
        {contractAddress && (
          <button
            onClick={handleAddToMetaMask}
            disabled={!isMetaMaskInstalled}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#0F0F0F] hover:bg-[#151515] border border-[#1A1A1A] hover:border-gold/40 rounded text-[10px] text-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={isMetaMaskInstalled ? 'Track this Digital Twin inside MetaMask' : 'MetaMask not detected'}
            data-testid="add-to-metamask-btn"
          >
            <PlusCircle size={10} />
            Add to MetaMask
          </button>
        )}
      </div>
    </div>
  );
};
